import { useTime, interpolate, Easing } from './animations';
import {
  AppShell,
  Avatar,
  ButtonGhost,
  ButtonPrimary,
  Chip,
  Cursor,
  FlagDot,
} from './ui';

// Scenes 5-8: Pathway generation → Bespoke lesson → Resource library → All students overview

// Realistic 12-month B1→B2 pathway for Mateo (Spanish architect, business goals)
const PATHWAY = [
  { m: 1, theme: "Foundations & rapport", color: "#2f5740", lessons: [
    "Self-introductions: work & life",
    "Talking about your studio (present simple)",
    "Asking for clarification & question forms",
    "Daily routines on a building site",
  ]},
  { m: 2, theme: "Past tenses", color: "#3a6248", lessons: [
    "Past simple: my career so far",
    "Past continuous: a project gone wrong",
    "Used to / would: how Madrid has changed",
    "Reading — the history of a famous building",
  ]},
  { m: 3, theme: "Future forms", color: "#446a4f", lessons: [
    "Will vs going to: industry predictions",
    "Present continuous for arrangements",
    "First conditional: contingency planning",
    "Listening — a project pitch",
  ]},
  { m: 4, theme: "Present perfect ★", color: "#a07a3e", lessons: [
    "Have you ever…? Portfolio talk",
    "For / since: years on a project",
    "Just / already / yet: progress updates",
    "Present perfect continuous: ongoing builds",
  ]},
  { m: 5, theme: "Workplace English I", color: "#5e8c7a", lessons: [
    "Phone calls with clients & contractors",
    "Booking & rescheduling meetings",
    "Email register: formal vs friendly",
    "Office vocabulary deep-dive",
  ]},
  { m: 6, theme: "Modals & advice", color: "#506e57", lessons: [
    "Should / ought to: feedback to a team",
    "Must / have to: site-safety language",
    "Could / might: hedging suggestions",
    "Roleplay — a design review",
  ]},
  { m: 7, theme: "Describing & comparing", color: "#5c785f", lessons: [
    "Comparing materials & finishes",
    "Adverbs of degree: very, quite, fairly",
    "So / such for emphasis",
    "Picture description practice",
  ]},
  { m: 8, theme: "Conditionals", color: "#688268", lessons: [
    "Second conditional: hypotheticals",
    "Third conditional: post-mortems",
    "Mixed conditionals",
    "Reading — design ethics",
  ]},
  { m: 9, theme: "Negotiations", color: "#8c6e4a", lessons: [
    "Polite disagreement",
    "Making proposals & counter-proposals",
    "Interrupting politely",
    "Roleplay — a budget meeting",
  ]},
  { m: 10, theme: "Narrative & reporting", color: "#6e5a8c", lessons: [
    "Reported statements",
    "Reported questions & commands",
    "Narrative tenses recap",
    "Writing — a project case study",
  ]},
  { m: 11, theme: "Presentations", color: "#1f3d2b", lessons: [
    "Structuring a talk",
    "Signposting: firstly, moving on, to summarise",
    "Visual-aids vocabulary",
    "Q&A — handling difficult questions",
  ]},
  { m: 12, theme: "IELTS bridge → B2", color: "#162b1e", lessons: [
    "IELTS Speaking parts 1 & 2",
    "IELTS Writing task 2",
    "Mock client interview",
    "Final assessment & reflection",
  ]},
];

// ────────────────────────────────────────────────────────────────────
// SCENE 5: Pathway generation — TeachESLPro 4-phase structure
// ────────────────────────────────────────────────────────────────────
const PHASES = [
  { n: 1, name: "Foundation",            color: "#2563eb", bg: "#eef4ff", border: "#bfd3ff",
    desc: "Consolidate B1 grammar gaps and build the workplace vocabulary spine.",
    objectives: ["Master present perfect & past tense contrast", "Acquire 400+ workplace vocabulary items", "Develop confident phone & meeting language"],
    tags: ["Present Perfect", "Past Tenses", "Workplace", "Email", "Meetings"],
    lessons: [
      ["Self-introductions: career & studio life", "speaking", "45min"],
      ["Present perfect vs past simple", "grammar", "45min"],
      ["Workplace vocabulary: the office", "vocabulary", "45min"],
      ["Phone calls with clients", "speaking", "60min"],
      ["Reading: a famous architect's CV", "reading", "45min"],
      ["Email register: formal vs friendly", "writing", "60min"],
      ["Listening: a project briefing", "listening", "45min"],
      ["Question forms in meetings", "grammar", "45min"],
    ]},
  { n: 2, name: "Skill Expansion",       color: "#0d9488", bg: "#e6f7f4", border: "#a8ddd2",
    desc: "Stretch into productive language — modals, conditionals, and longer-form output.",
    tags: ["Conditionals", "Modals", "Comparing", "Describing"],
    lessons: [] },
  { n: 3, name: "Controlled Fluency",    color: "#7c3aed", bg: "#f1ecfd", border: "#d4c2f5",
    desc: "Negotiation, narrative, and presentation skills under realistic constraints.",
    tags: ["Negotiation", "Reported speech", "Presenting"],
    lessons: [] },
  { n: 4, name: "Independent Performance", color: "#16a34a", bg: "#eaf6ec", border: "#bcdfc1",
    desc: "Real-world output: client interviews, IELTS practice, full case studies.",
    tags: ["IELTS bridge", "Case study", "Mock interview"],
    lessons: [] },
];

function Scene5() {
  const t = useTime();
  // 0-0.6 header + sidebar fade in
  // 0.6-2.0 phases stagger in (collapsed)
  // 2.0-2.4 Phase 1 Foundation expands
  // 2.4-4.6 lessons populate, sidebar skill bars tick
  // 4.6-5.0 progress nibble + glow

  const headerP = Math.min(1, Math.max(0, t / 0.5));
  const expandP = Math.min(1, Math.max(0, (t - 2.0) / 0.4));

  return (
    <div style={{ width: 1280, height: 720, background: "#f4efe2", position: "relative" }}>
      <AppShell active="pathways" breadcrumb="STUDENTS / MATEO RAMÍREZ" title=""
        topRight={<>
          <ButtonGhost icon={<svg width="13" height="13" viewBox="0 0 14 14"><path d="M3 3h8v8H3z M5 1v3 M9 1v3" stroke="currentColor" strokeWidth="1.4" fill="none"/></svg>}>Link Collection</ButtonGhost>
          <div style={{ background: "#16a34a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="12" height="12" viewBox="0 0 14 14"><path d="M7 1v8 M3 5l4-4 4 4 M2 11h10" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round"/></svg>
            Share
          </div>
          <ButtonGhost icon={<svg width="13" height="13" viewBox="0 0 14 14"><path d="M2 7a5 5 0 019-3 M12 7a5 5 0 01-9 3 M9 1v3h3 M5 13v-3H2" stroke="#0d9488" strokeWidth="1.4" fill="none" strokeLinecap="round"/></svg>}><span style={{ color: "#0d9488" }}>Regenerate</span></ButtonGhost>
        </>}>

        <div style={{ padding: "14px 24px 18px", height: "100%", display: "grid", gridTemplateColumns: "1fr 248px", gap: 16, overflow: "hidden" }}>
          {/* LEFT: pathway phases */}
          <div style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {/* Title strip */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: headerP, transform: `translateY(${(1-headerP)*6}px)`, marginBottom: 12 }}>
              <svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="5" r="3" stroke="#161a17" strokeWidth="1.5" fill="none"/><path d="M2 13c0-3 2-5 5-5s5 2 5 5" stroke="#161a17" strokeWidth="1.5" fill="none"/></svg>
              <span style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 600, color: "#161a17" }}>Mateo Ramírez&rsquo;s Learning Pathway</span>
              <span style={{ fontSize: 11.5, color: "#6b7269", marginLeft: 4 }}>B1 → Next level</span>
            </div>

            {/* Timeline rail with phase rows */}
            <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 10 }}>
              {/* vertical timeline rail */}
              <div style={{ position: "absolute", left: 8, top: 24, bottom: 24, width: 1, background: "#d8d2bd", opacity: headerP }}/>

              {PHASES.map((p, i) => {
                const start = 0.5 + i * 0.18;
                const rowP = Math.min(1, Math.max(0, (t - start) / 0.35));
                const isExpanded = i === 0;
                const showLessons = isExpanded && expandP > 0;
                return (
                  <div key={p.n} style={{
                    position: "relative", paddingLeft: 22,
                    opacity: rowP, transform: `translateY(${(1-rowP)*6}px)`,
                    flex: isExpanded && showLessons ? 1 : "none",
                    minHeight: 0,
                  }}>
                    {/* dot on rail */}
                    <div style={{
                      position: "absolute", left: 4, top: 22,
                      width: 9, height: 9, borderRadius: 9,
                      border: "2px solid #b3ad97", background: "#f4efe2",
                    }}/>
                    {/* phase header */}
                    <div style={{
                      background: p.bg,
                      border: `1.5px solid ${i === 0 ? p.color : p.border}`,
                      borderRadius: 10, padding: "10px 14px",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: 26,
                        background: p.color, color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, fontWeight: 700, fontFamily: "var(--ui)",
                        flexShrink: 0,
                      }}>{p.n}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: p.color, lineHeight: 1.1 }}>{p.name}</div>
                        <div style={{ fontSize: 10.5, color: "#6b7269", marginTop: 2 }}>0/24 lessons</div>
                      </div>
                      <div style={{ width: 80, height: 4, background: "#e2dcc8", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: `${isExpanded ? Math.min(8, expandP * 8) : 0}%`, height: "100%", background: p.color, transition: "width 200ms" }}/>
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7269", fontFamily: "var(--mono)", width: 28, textAlign: "right" }}>0%</div>
                      <svg width="14" height="14" viewBox="0 0 14 14" style={{ transform: `rotate(${isExpanded ? 180 * expandP : 0}deg)`, transition: "transform 200ms", color: p.color }}>
                        <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>

                    {/* Expanded body for Phase 1 */}
                    {showLessons && (
                      <div style={{
                        opacity: expandP, marginTop: 10, paddingLeft: 4,
                        display: "flex", flexDirection: "column", gap: 8, overflow: "hidden",
                      }}>
                        <div style={{ fontSize: 11.5, color: "#2c322d", lineHeight: 1.5 }}>{p.desc}</div>
                        {/* Objectives */}
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: "#161a17", marginBottom: 4 }}>
                            <svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5" stroke="#161a17" strokeWidth="1.2" fill="none"/><circle cx="6" cy="6" r="2" fill="#161a17"/></svg>
                            Objectives
                          </div>
                          <ul style={{ margin: 0, padding: "0 0 0 18px", fontSize: 11, color: "#2c322d", lineHeight: 1.55 }}>
                            {p.objectives.map((o, oi) => <li key={oi}>{o}</li>)}
                          </ul>
                        </div>
                        {/* Tag chips */}
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {p.tags.map(tg => <span key={tg} style={{ fontSize: 10.5, color: p.color, padding: "2px 7px", background: "#fff", border: `1px solid ${p.border}`, borderRadius: 999 }}>{tg}</span>)}
                        </div>
                        {/* Lessons header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: "#161a17", marginTop: 2 }}>
                          <svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5" stroke="#161a17" strokeWidth="1.2" fill="none"/></svg>
                          Lessons
                        </div>
                        {/* Lessons list */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 5, overflow: "hidden" }}>
                          {p.lessons.map((l, li) => {
                            const ls = 2.4 + li * 0.18;
                            const lp = Math.min(1, Math.max(0, (t - ls) / 0.3));
                            return (
                              <LessonRow key={li} lesson={l} progress={lp} color={p.color} idx={li}/>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, overflow: "hidden", opacity: headerP }}>
            <SidebarCard title="OVERALL PROGRESS">
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0 4px" }}>
                <RingChart pct={Math.min(2, t > 4.6 ? (t - 4.6) * 4 : 0)}/>
                <div style={{ fontSize: 10.5, color: "#6b7269", marginTop: 4 }}>0 of 96 lessons</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
                <MiniStat icon="📘" label="Current Level" value="B1"/>
                <MiniStat icon="🎯" label="Target Level" value="Next"/>
                <MiniStat icon="⏱" label="Timeframe" value="12mo"/>
                <MiniStat icon="📈" label="Lessons/week" value="2/wk"/>
              </div>
            </SidebarCard>

            <SidebarCard title="SKILL DEVELOPMENT">
              {[
                ["Writing", 0], ["Grammar", 0], ["Reading", 0], ["Listening", 0],
                ["Speaking", 0], ["Pronunciation", 0], ["Vocabulary", 0],
              ].map(([s], i) => {
                const sp = Math.min(1, Math.max(0, (t - 0.6 - i * 0.06) / 0.3));
                return (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", opacity: sp }}>
                    <div style={{ flex: 1, fontSize: 11, color: "#2c322d" }}>{s}</div>
                    <div style={{ width: 70, height: 3, background: "#e8e3d2", borderRadius: 2 }}/>
                    <div style={{ width: 22, fontSize: 10, color: "#6b7269", textAlign: "right", fontFamily: "var(--mono)" }}>0%</div>
                  </div>
                );
              })}
            </SidebarCard>

            <SidebarCard title="STUDENT NOTES" style={{ flex: 1, minHeight: 0 }}>
              <div style={{ fontSize: 11, color: "#8a8f86", padding: "8px 0", lineHeight: 1.4 }}>
                Add general notes about this student...
              </div>
            </SidebarCard>

            {/* Pathway benefit callout */}
            {t > 4.4 && (
              <div style={{
                position: "absolute", right: 24, bottom: 24,
                background: "#1f3d2b", color: "#faf6ed",
                padding: "10px 14px", borderRadius: 10, fontSize: 11.5,
                maxWidth: 220, opacity: Math.min(1, (t - 4.4) / 0.3),
                boxShadow: "0 18px 40px -12px rgba(0,0,0,0.3)",
              }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>✨ 96 lessons mapped</div>
                <div style={{ color: "#c7b89a", lineHeight: 1.4 }}>Every week is planned, every skill is tracked.</div>
              </div>
            )}
          </div>
        </div>
      </AppShell>
    </div>
  );
}

function LessonRow({ lesson, progress }) {
  const [title, kind, dur] = lesson;
  const kindColors = {
    grammar: { bg: "#dbeafe", fg: "#1e40af" },
    speaking: { bg: "#fee2c4", fg: "#9a3412" },
    listening: { bg: "#fce7f3", fg: "#9d174d" },
    reading: { bg: "#fef3c7", fg: "#854d0e" },
    writing: { bg: "#fef3c7", fg: "#854d0e" },
    vocabulary: { bg: "#e0e7ff", fg: "#3730a3" },
  };
  const k = kindColors[kind] || kindColors.grammar;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e8e3d2", borderRadius: 8,
      padding: "8px 12px",
      opacity: progress,
      transform: `translateY(${(1-progress)*4}px)`,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{ width: 12, height: 12, borderRadius: 12, border: "1.5px solid #d8d2bd", flexShrink: 0 }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#161a17", lineHeight: 1.2 }}>{title}</div>
        <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
          <span style={{ fontSize: 9.5, padding: "1px 6px", background: k.bg, color: k.fg, borderRadius: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{kind}</span>
          <span style={{ fontSize: 10, color: "#6b7269" }}>{dur}</span>
          <span style={{ fontSize: 10, color: "#0d9488" }}>🔗 Linked</span>
          <span style={{ fontSize: 10, color: "#6b7269" }}>✎ Change</span>
        </div>
      </div>
    </div>
  );
}

function SidebarCard({ title, children, style = {} }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 10, padding: "12px 14px", ...style }}>
      <div style={{ fontSize: 9.5, color: "#8a8f86", fontFamily: "var(--mono)", letterSpacing: "0.08em", fontWeight: 600 }}>{title}</div>
      {children}
    </div>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div style={{ background: "#faf6ed", border: "1px solid #e8e3d2", borderRadius: 6, padding: "6px 8px", textAlign: "center" }}>
      <div style={{ fontSize: 12 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1f3d2b", marginTop: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color: "#8a8f86" }}>{label}</div>
    </div>
  );
}

function RingChart({ pct }) {
  const r = 32, c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  return (
    <div style={{ position: "relative", width: 80, height: 80 }}>
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} stroke="#e8e3d2" strokeWidth="6" fill="none"/>
        <circle cx="40" cy="40" r={r} stroke="#0d9488" strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 40 40)"/>
        <circle cx="40" cy="8" r="3" fill="#0d9488"/>
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--display)", fontSize: 18, fontWeight: 700, color: "#161a17" }}>{Math.round(pct)}%</div>
    </div>
  );
}

// Local helper used by Scene 6's bespoke-request modal (was a global in the prototype).
function Label({ children, style = {} }) {
  return (
    <div style={{
      fontFamily: "var(--mono)", fontSize: 10, color: "#8a8f86",
      textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6,
      ...style,
    }}>{children}</div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SCENE 6: Request bespoke lesson
// ────────────────────────────────────────────────────────────────────
function Scene6() {
  const t = useTime();
  // 0-0.8 cursor moves to a lesson card on month 5, week 3
  // 0.8-1.4 click → context menu opens
  // 1.4-2.2 cursor to "Request bespoke lesson" → click → modal opens
  // 2.2-4.4 modal visible; teacher types prompt
  // 4.4-5.0 generate clicked → "queued" state
  const cursor = {
    x: interpolate([0, 0.8, 1.5, 2.2, 4.5, 5], [800, 720, 560, 640, 760, 760], Easing.easeInOutCubic)(t),
    y: interpolate([0, 0.8, 1.5, 2.2, 4.5, 5], [400, 330, 380, 480, 540, 540], Easing.easeInOutCubic)(t),
  };
  const menuOpen = t > 0.8 && t < 1.6;
  const modalOpen = t > 1.6;
  const generated = t > 4.4;

  // Promo text typewriter
  const promptText = "Focus on emails to Italian construction suppliers — Mateo is starting a project in Milan next month and wants to be confident with formal request phrasing.";
  const typed = t < 2.4 ? "" : promptText.slice(0, Math.min(promptText.length, Math.floor((t - 2.4) / 1.7 * promptText.length)));

  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative" }}>
      <AppShell active="pathways" breadcrumb="STUDENTS / MATEO RAMÍREZ / PATHWAY" title="12-month pathway · M5 Workplace English"
        topRight={<><ButtonGhost>Edit</ButtonGhost><ButtonPrimary>+ Add lesson</ButtonPrimary></>}>
        <div style={{ padding: "16px 28px", height: "100%", position: "relative", overflow: "hidden" }}>
          {/* Mini month nav */}
          <div style={{ display: "flex", gap: 4, padding: "0 0 12px" }}>
            {PATHWAY.map(m => (
              <div key={m.m} style={{
                flex: 1, padding: "6px 0", textAlign: "center",
                background: m.m === 5 ? "#1f3d2b" : "#fff",
                color: m.m === 5 ? "#faf6ed" : "#6b7269",
                border: "1px solid", borderColor: m.m === 5 ? "#1f3d2b" : "#e8e3d2",
                borderRadius: 6, fontSize: 10.5, fontFamily: "var(--mono)", fontWeight: 600,
                letterSpacing: "0.04em",
              }}>M{String(m.m).padStart(2,"0")}</div>
            ))}
          </div>

          {/* Month 5 detail */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {PATHWAY[4].lessons.map((l, i) => {
              const isTarget = i === 2; // "Email register: formal vs friendly"
              return (
                <div key={i} style={{
                  background: "#fff", border: "1px solid #e8e3d2",
                  borderLeft: "3px solid #5e8c7a",
                  borderRadius: 10, padding: 14,
                  position: "relative",
                  boxShadow: isTarget && menuOpen ? "0 0 0 2px #1f3d2b" : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "#8a8f86", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Wk {17 + i} · Mon 7 Sep 2026
                    </span>
                    <span style={{ fontSize: 10, color: "#8a8f86", fontFamily: "var(--mono)" }}>50 min</span>
                  </div>
                  <div style={{ fontFamily: "var(--display)", fontSize: 16, fontWeight: 500, marginTop: 6, lineHeight: 1.25 }}>{l}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                    <Chip color="cream" size="sm">B1</Chip>
                    <Chip color="cream" size="sm">{["Speaking","Reading","Writing","Listening"][i]}</Chip>
                    {isTarget && <Chip color="tan" size="sm">★ in‑demand</Chip>}
                  </div>

                  {/* Context menu on isTarget */}
                  {isTarget && menuOpen && (
                    <div style={{
                      position: "absolute", left: 200, top: 70,
                      background: "#fff", border: "1px solid #d8d2bd", borderRadius: 10,
                      padding: 6, width: 220, zIndex: 30,
                      boxShadow: "0 18px 40px -12px rgba(0,0,0,0.25)",
                      opacity: Math.min(1, (t - 0.8) / 0.2),
                      transform: `translateY(${(1 - Math.min(1, (t - 0.8) / 0.2)) * -6}px)`,
                    }}>
                      <MenuItem icon="✎">Edit lesson</MenuItem>
                      <MenuItem icon="↻">Swap for alternative</MenuItem>
                      <MenuItem icon="✶" highlighted={t > 1.3}>Request bespoke lesson</MenuItem>
                      <MenuItem icon="↗">Move to another week</MenuItem>
                      <MenuItem icon="⌫" danger>Skip this week</MenuItem>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bespoke modal */}
          {modalOpen && (
            <div style={{
              position: "absolute", inset: 0,
              background: "rgba(20, 26, 22, 0.30)",
              backdropFilter: "blur(2px)",
              zIndex: 40,
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: Math.min(1, (t - 1.6) / 0.3),
            }}>
              <div style={{
                width: 580, background: "#fff",
                border: "1px solid #d8d2bd", borderRadius: 16,
                padding: 24, boxShadow: "0 30px 80px -30px rgba(0,0,0,0.5)",
                transform: `translateY(${(1 - Math.min(1, (t - 1.6) / 0.4)) * 12}px) scale(${0.96 + Math.min(1, (t - 1.6) / 0.4) * 0.04})`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e6ede0", display: "flex", alignItems: "center", justifyContent: "center", color: "#1f3d2b", fontSize: 16 }}>✶</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, color: "#8a8f86", textTransform: "uppercase", letterSpacing: "0.08em" }}>Bespoke lesson · Wk 19 · Mateo</div>
                    <div style={{ fontFamily: "var(--display)", fontSize: 19, fontWeight: 500 }}>Replacing — Email register: formal vs friendly</div>
                  </div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <Label>What should this lesson focus on?</Label>
                  <div style={{
                    border: "1px solid #d8d2bd", borderRadius: 10, padding: 14,
                    minHeight: 88, fontSize: 13.5, lineHeight: 1.55, color: "#2c322d",
                    background: "#fbf8ee",
                  }}>
                    {typed}
                    {!generated && t > 2.4 && <span className="tep-caret" style={{ color: "#1f3d2b" }}>|</span>}
                  </div>
                </div>

                <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 11, color: "#6b7269", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: "0.06em", width: "100%" }}>Quick add</div>
                  <Chip color="cream" size="sm">+ business email phrases</Chip>
                  <Chip color="cream" size="sm">+ Italian/English false friends</Chip>
                  <Chip color="cream" size="sm">+ roleplay drill</Chip>
                  <Chip color="cream" size="sm">+ writing task</Chip>
                </div>

                {generated && (
                  <div style={{
                    marginTop: 16, padding: 14,
                    background: "#1f3d2b", color: "#faf6ed",
                    borderRadius: 10,
                    display: "flex", alignItems: "center", gap: 12,
                    opacity: Math.min(1, (t - 4.4) / 0.3),
                  }}>
                    <div style={{ width: 28, height: 28, borderRadius: 28, background: "#c7b89a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7l3 3 5-6" stroke="#1f3d2b" strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round"/></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Lesson queued · ready in ~3 minutes</div>
                      <div style={{ fontSize: 11.5, color: "#c7b89a" }}>You&rsquo;ll get a draft to review before Mateo sees it.</div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 11, color: "#8a8f86", fontFamily: "var(--mono)" }}>1 of 6 bespoke slots used this year</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <ButtonGhost>Cancel</ButtonGhost>
                    <div style={{
                      background: generated ? "#162b1e" : "#1f3d2b",
                      color: "#faf6ed", padding: "9px 18px", borderRadius: 8,
                      fontSize: 13, fontWeight: 600,
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}>
                      {generated ? "Queued" : "Generate lesson →"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Cursor x={cursor.x} y={cursor.y} label={t > 1.4 && t < 1.6 ? "click" : (t > 4.3 && t < 4.5 ? "click" : null)}/>
        </div>
      </AppShell>
    </div>
  );
}

function MenuItem({ icon, children, highlighted, danger }) {
  return (
    <div style={{
      padding: "8px 10px", borderRadius: 6,
      display: "flex", alignItems: "center", gap: 10,
      background: highlighted ? "#e6ede0" : "transparent",
      color: danger ? "#a04020" : "#1f3d2b",
      fontSize: 12.5, fontWeight: highlighted ? 600 : 500,
    }}>
      <span style={{ width: 16, fontSize: 12, color: highlighted ? "#1f3d2b" : "#8a8f86", textAlign: "center" }}>{icon}</span>
      <span>{children}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SCENE 7: Resource library
// ────────────────────────────────────────────────────────────────────
function Scene7() {
  const t = useTime();
  // 0-0.8 page loads, filters slide in
  // 0.8-2.5 cards populate stagger
  // 2.5-3.4 click "B1" filter — cards rearrange with fade
  // 3.4-4.6 hover a card → preview hover lifts
  // 4.6-5.0 "Add to lesson" clicked

  const allResources = [
    { id: 1, title: "Polite emails to clients", kind: "Worksheet", level: "B1", duration: "20 min", color: "#5e8c7a" },
    { id: 2, title: "Site-safety briefing audio", kind: "Listening", level: "A2", duration: "8 min", color: "#a07a3e" },
    { id: 3, title: "Negotiating fees roleplay", kind: "Roleplay", level: "B2", duration: "30 min", color: "#6e5a8c" },
    { id: 4, title: "Reading: Bilbao&rsquo;s Guggenheim", kind: "Reading", level: "B1", duration: "15 min", color: "#5e8c7a" },
    { id: 5, title: "Present perfect drill — pro builds", kind: "Grammar", level: "B1", duration: "25 min", color: "#1f3d2b" },
    { id: 6, title: "Phrasal verbs at the office", kind: "Vocabulary", level: "B2", duration: "18 min", color: "#6e5a8c" },
    { id: 7, title: "Watch: How a project gets pitched", kind: "Video", level: "B1", duration: "11 min", color: "#5e8c7a" },
    { id: 8, title: "Conditional sentences at work", kind: "Grammar", level: "B1", duration: "22 min", color: "#1f3d2b" },
    { id: 9, title: "IELTS speaking part 2 prompts", kind: "Speaking", level: "B2", duration: "30 min", color: "#6e5a8c" },
    { id: 10, title: "Talking about timelines", kind: "Speaking", level: "A2", duration: "12 min", color: "#a07a3e" },
    { id: 11, title: "Writing a project case study", kind: "Writing", level: "B1", duration: "45 min", color: "#5e8c7a" },
    { id: 12, title: "Listening: a design review", kind: "Listening", level: "B2", duration: "9 min", color: "#6e5a8c" },
  ];

  const filterApplied = t > 2.5;
  const visible = filterApplied
    ? allResources.filter(r => r.level === "B1")
    : allResources;
  const previewId = t > 3.4 ? 4 : null;
  const adding = t > 4.55;

  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative" }}>
      <AppShell active="library" breadcrumb="LIBRARY" title="Resource Library"
        topRight={<><ButtonGhost icon="⤓">Import</ButtonGhost><ButtonPrimary>+ New resource</ButtonPrimary></>}>
        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", height: "100%" }}>
          {/* Filter rail */}
          <div style={{ borderRight: "1px solid #e8e3d2", padding: "16px 18px", background: "#fbf8ee", overflow: "auto" }}>
            <Label>Skill</Label>
            <FilterCol items={["All","Speaking","Listening","Reading","Writing","Grammar","Vocabulary","Roleplay","Video"]} selected={"All"}/>
            <Label style={{ marginTop: 16 }}>CEFR level</Label>
            <FilterCol items={["A1","A2","B1","B2","C1"]} selected={filterApplied ? "B1" : null} highlight="B1" highlightT={2.5} t={t}/>
            <Label style={{ marginTop: 16 }}>Duration</Label>
            <FilterCol items={["< 10 min","10–20 min","20–40 min","40+ min"]} selected={null}/>
            <Label style={{ marginTop: 16 }}>Source</Label>
            <FilterCol items={["TeachESLPro","Cambridge","Macmillan","BBC","My uploads"]} selected={null}/>
          </div>

          {/* Grid */}
          <div style={{ padding: "14px 22px", overflow: "hidden", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 12.5, color: "#6b7269" }}>
                Showing {visible.length} of {allResources.length} resources
                {filterApplied && <Chip color="sage" size="sm" style={{ marginLeft: 10 }}>level: B1 ✕</Chip>}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5, color: "#6b7269" }}>
                Sort by <span style={{ fontWeight: 600, color: "#1f3d2b" }}>Recommended for Mateo</span> ▾
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, alignContent: "start" }}>
              {visible.map((r, idx) => {
                const start = filterApplied ? 2.6 + idx * 0.06 : 0.6 + idx * 0.10;
                const p = Math.min(1, Math.max(0, (t - start) / 0.4));
                const hovered = r.id === previewId;
                return (
                  <div key={r.id} style={{
                    background: "#fff", border: "1px solid #e8e3d2", borderRadius: 10,
                    padding: 12, opacity: p,
                    transform: `translateY(${(1-p)*10}px) scale(${hovered ? 1.025 : 1})`,
                    boxShadow: hovered ? "0 18px 40px -12px rgba(0,0,0,0.20)" : "none",
                    transition: "transform 200ms, box-shadow 200ms",
                    position: "relative",
                  }}>
                    <div style={{ height: 64, borderRadius: 6, background: `linear-gradient(135deg, ${r.color}, ${r.color}cc)`, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ResourceGlyph kind={r.kind}/>
                      <div style={{ position: "absolute", top: 6, right: 6 }}>
                        <Chip color="cream" size="sm" style={{ background: "rgba(250,246,237,0.95)" }}>{r.level}</Chip>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }} dangerouslySetInnerHTML={{__html: r.title}}/>
                    <div style={{ marginTop: 4, fontSize: 10.5, color: "#8a8f86", display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)" }}>
                      <span>{r.kind.toUpperCase()}</span>
                      <span>{r.duration}</span>
                    </div>
                    {hovered && (
                      <div style={{
                        position: "absolute", left: 0, right: 0, bottom: 0,
                        padding: 12, background: "rgba(255,255,255,0.96)", borderTop: "1px solid #e8e3d2",
                        borderRadius: "0 0 10px 10px",
                        display: "flex", gap: 6,
                        opacity: Math.min(1, (t - 3.4) / 0.3),
                      }}>
                        <div style={{ flex: 1, padding: "6px 10px", border: "1px solid #d8d2bd", borderRadius: 6, fontSize: 11, textAlign: "center" }}>Preview</div>
                        <div style={{
                          flex: 1, padding: "6px 10px", borderRadius: 6, fontSize: 11, textAlign: "center", fontWeight: 600,
                          background: adding ? "#162b1e" : "#1f3d2b",
                          color: "#faf6ed",
                          transform: t > 4.6 && t < 4.8 ? "scale(0.96)" : "scale(1)",
                          transition: "transform 80ms",
                        }}>
                          {adding ? "✓ Added" : "+ Lesson"}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Cursor */}
            <Cursor
              x={interpolate([0, 0.8, 2.4, 3.4, 4.55, 5], [820, 360, 100, 740, 740, 740], Easing.easeInOutCubic)(t)}
              y={interpolate([0, 0.8, 2.4, 3.4, 4.55, 5], [600, 480, 300, 290, 410, 410], Easing.easeInOutCubic)(t)}
              label={t > 2.4 && t < 2.6 ? "filter B1" : (t > 4.55 && t < 4.7 ? "add" : null)}
            />
          </div>
        </div>
      </AppShell>
    </div>
  );
}

function FilterCol({ items, selected, highlight, highlightT, t }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
      {items.map((x) => {
        const isHighlighted = highlight && x === highlight && t >= highlightT;
        const isSelected = x === selected;
        return (
          <div key={x} style={{
            padding: "5px 8px", borderRadius: 5,
            background: isSelected ? "#1f3d2b" : (isHighlighted ? "#e6ede0" : "transparent"),
            color: isSelected ? "#faf6ed" : "#2c322d",
            fontSize: 12, fontWeight: isSelected ? 600 : 500,
            display: "flex", alignItems: "center", gap: 8,
            transition: "background 150ms",
          }}>
            <span style={{
              width: 10, height: 10, borderRadius: 2,
              border: `1.5px solid ${isSelected ? "#faf6ed" : "#b3ad97"}`,
              background: isSelected ? "#faf6ed" : "transparent",
              display: "inline-block",
            }}/>
            {x}
          </div>
        );
      })}
    </div>
  );
}

function ResourceGlyph({ kind }) {
  const map = {
    "Worksheet": <svg width="32" height="32" viewBox="0 0 32 32"><rect x="8" y="6" width="16" height="20" rx="2" fill="#faf6ed" opacity="0.9"/><line x1="11" y1="11" x2="21" y2="11" stroke="#1f3d2b" strokeWidth="1.5"/><line x1="11" y1="15" x2="19" y2="15" stroke="#1f3d2b" strokeWidth="1.5"/><line x1="11" y1="19" x2="20" y2="19" stroke="#1f3d2b" strokeWidth="1.5"/></svg>,
    "Listening": <svg width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="9" fill="#faf6ed" opacity="0.9"/><path d="M14 12v8M18 12v8M12 14v4M20 14v4" stroke="#1f3d2b" strokeWidth="1.6" strokeLinecap="round"/></svg>,
    "Roleplay": <svg width="32" height="32" viewBox="0 0 32 32"><circle cx="12" cy="14" r="4" fill="#faf6ed" opacity="0.9"/><circle cx="20" cy="14" r="4" fill="#faf6ed" opacity="0.6"/><path d="M8 24c0-4 4-5 4-5s4 1 4 5M16 24c0-4 4-5 4-5s4 1 4 5" stroke="#faf6ed" strokeWidth="1.5" fill="none"/></svg>,
    "Reading": <svg width="32" height="32" viewBox="0 0 32 32"><path d="M6 8h10v18H6z M16 8h10v18H16z M16 8c-2 0-2 0-4 1c-2-1-4-1-6-1M16 8c2 0 2 0 4 1c2-1 4-1 6-1" stroke="#faf6ed" strokeWidth="1.5" fill="none"/></svg>,
    "Grammar": <svg width="32" height="32" viewBox="0 0 32 32"><text x="8" y="22" fontFamily="var(--display)" fontSize="18" fontWeight="600" fill="#faf6ed">Aa</text></svg>,
    "Vocabulary": <svg width="32" height="32" viewBox="0 0 32 32"><rect x="6" y="11" width="9" height="3" fill="#faf6ed"/><rect x="17" y="11" width="9" height="3" fill="#faf6ed" opacity="0.7"/><rect x="6" y="16" width="13" height="3" fill="#faf6ed" opacity="0.7"/><rect x="6" y="21" width="6" height="3" fill="#faf6ed"/></svg>,
    "Speaking": <svg width="32" height="32" viewBox="0 0 32 32"><rect x="13" y="8" width="6" height="12" rx="3" fill="#faf6ed"/><path d="M9 16a7 7 0 0014 0M16 23v3M12 26h8" stroke="#faf6ed" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>,
    "Writing": <svg width="32" height="32" viewBox="0 0 32 32"><path d="M22 6l4 4-12 12-5 1 1-5z" fill="#faf6ed"/></svg>,
    "Video": <svg width="32" height="32" viewBox="0 0 32 32"><rect x="6" y="9" width="20" height="14" rx="2" fill="#faf6ed"/><path d="M14 13l5 3-5 3z" fill="#1f3d2b"/></svg>,
  };
  return map[kind] || <div style={{ color: "#faf6ed", fontFamily: "var(--mono)", fontSize: 11 }}>{kind}</div>;
}

// ────────────────────────────────────────────────────────────────────
// SCENE 8: All-students progress overview
// ────────────────────────────────────────────────────────────────────
function Scene8() {
  const t = useTime();

  const stats = [
    { label: "Active students", value: 12, delta: "+2 this month", t: 0.4 },
    { label: "Lessons this week", value: 28, delta: "11 / 12 on schedule", t: 0.6 },
    { label: "Avg pathway progress", value: "47%", delta: "+4pt vs last month", t: 0.8 },
    { label: "Assessments due", value: 3, delta: "Yuki, Hyejin, Linh", t: 1.0 },
  ];

  const students = [
    { name: "Yuki Tanaka",     country: "JP", level: "B2", week: 31, sessions: 4, last: "1d ago",  status: "ok",   trend: [40,42,45,49,52,56,61,64,67] },
    { name: "Hyejin Park",     country: "KR", level: "C1", week: 43, sessions: 5, last: "4h ago",  status: "ok",   trend: [60,62,66,70,74,77,80,82,84] },
    { name: "Lucas Almeida",   country: "BR", level: "B1", week: 26, sessions: 3, last: "3h ago",  status: "ok",   trend: [25,28,32,36,40,44,47,49,51] },
    { name: "Mateo Ramírez",   country: "ES", level: "B1", week: 1,  sessions: 1, last: "today",   status: "new",  trend: [0,0,0,0,0,0,0,0,2], flag: "★ new" },
    { name: "Aylin Demir",     country: "TR", level: "A2", week: 14, sessions: 2, last: "2d ago",  status: "warn", trend: [10,12,16,18,20,22,24,26,28], flag: "missed 2" },
    { name: "Linh Nguyen",     country: "VN", level: "B1", week: 19, sessions: 3, last: "4h ago",  status: "ok",   trend: [15,18,22,26,29,32,34,37,39] },
    { name: "Pierre Laurent",  country: "FR", level: "B2", week: 38, sessions: 4, last: "1d ago",  status: "ok",   trend: [50,53,58,62,66,69,71,72,73] },
    { name: "Greta Bauer",     country: "DE", level: "C1", week: 47, sessions: 4, last: "2h ago",  status: "ok",   trend: [70,72,75,79,82,84,86,88,90] },
    { name: "Nadia Haddad",    country: "SA", level: "A2", week: 10, sessions: 2, last: "5d ago",  status: "warn", trend: [8,10,12,14,16,18,18,19,20], flag: "at risk" },
    { name: "Sofia Rossi",     country: "IT", level: "B1", week: 22, sessions: 3, last: "1d ago",  status: "ok",   trend: [22,24,28,31,34,37,40,42,44] },
    { name: "Tomás Silva",     country: "BR", level: "B1", week: 17, sessions: 2, last: "today",   status: "ok",   trend: [12,15,18,22,25,28,30,33,35] },
    { name: "Min‑seo Kim",     country: "KR", level: "B2", week: 35, sessions: 4, last: "1d ago",  status: "ok",   trend: [42,46,49,53,57,60,63,65,68] },
  ];

  return (
    <div style={{ width: 1280, height: 720, background: "#ece6d4", position: "relative" }}>
      <AppShell active="home" breadcrumb="HOME" title="Good morning, Sarah"
        topRight={<><ButtonGhost>Calendar</ButtonGhost><ButtonGhost>Reports</ButtonGhost><ButtonPrimary>+ New student</ButtonPrimary></>}>
        <div style={{ padding: "16px 24px", height: "100%", overflow: "hidden", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* KPI strip */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {stats.map((s) => {
              const p = Math.min(1, Math.max(0, (t - s.t) / 0.5));
              return (
                <div key={s.label} style={{
                  background: "#fff", border: "1px solid #e8e3d2", borderRadius: 12, padding: "14px 16px",
                  opacity: p, transform: `translateY(${(1-p)*8}px)`,
                }}>
                  <div style={{ fontSize: 10.5, color: "#8a8f86", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                  <div style={{ fontFamily: "var(--display)", fontSize: 28, fontWeight: 500, marginTop: 2, letterSpacing: "-0.01em" }}>
                    <Counter value={s.value} progress={p}/>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#5e8c7a", marginTop: 4 }}>{s.delta}</div>
                </div>
              );
            })}
          </div>

          {/* Layout: students table + side weekly heatmap */}
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 14, flex: 1, minHeight: 0 }}>
            {/* Students table */}
            <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #e8e3d2" }}>
                <div style={{ fontFamily: "var(--display)", fontSize: 16, fontWeight: 500 }}>Your students · 12</div>
                <div style={{ display: "flex", gap: 6, fontSize: 11, color: "#6b7269" }}>
                  <Chip color="cream" size="sm">All</Chip>
                  <Chip color="cream" size="sm">Active</Chip>
                  <Chip color="tan" size="sm">⚠ Needs attention · 2</Chip>
                </div>
              </div>

              <div style={{
                display: "grid", gridTemplateColumns: "1.6fr 0.6fr 0.7fr 1fr 1.3fr 0.6fr",
                padding: "8px 16px", borderBottom: "1px solid #e8e3d2",
                fontSize: 10.5, color: "#8a8f86", fontFamily: "var(--mono)",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                <div>Student</div><div>CEFR</div><div>Wk / 52</div><div>Trend (90d)</div><div>This week</div><div style={{textAlign:"right"}}>Last</div>
              </div>

              <div style={{ overflow: "hidden", flex: 1 }}>
                {students.map((s, i) => {
                  const start = 1.0 + i * 0.08;
                  const p = Math.min(1, Math.max(0, (t - start) / 0.4));
                  return (
                    <div key={s.name} style={{
                      display: "grid", gridTemplateColumns: "1.6fr 0.6fr 0.7fr 1fr 1.3fr 0.6fr",
                      padding: "9px 16px", alignItems: "center",
                      borderBottom: i < students.length - 1 ? "1px solid #f3eedf" : "none",
                      fontSize: 12.5,
                      opacity: p, transform: `translateX(${(1-p) * -8}px)`,
                      background: s.status === "warn" ? "rgba(192, 96, 32, 0.04)" : "transparent",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <Avatar name={s.name} color={["#7a8c5a","#5e8c7a","#8c6e4a","#7a8c5a","#a07a3e","#5e8c7a","#6e5a8c","#4a6b52","#a07a3e","#8c6e4a","#5e8c7a","#6e5a8c"][i]} size={24}/>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                            <FlagDot country={s.country} size={9}/>
                            {s.flag && <span style={{ fontSize: 9.5, color: s.status === "warn" ? "#a04020" : "#a07a3e", fontFamily: "var(--mono)" }}>{s.flag}</span>}
                          </div>
                        </div>
                      </div>
                      <div><Chip color={s.level === "C1" ? "ink" : "sage"} size="sm">{s.level}</Chip></div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#2c322d" }}>{s.week} / 52</div>
                      <div><Sparkline data={s.trend} progress={p} status={s.status}/></div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {Array.from({length:5}).map((_,d) => (
                          <div key={d} style={{
                            width: 10, height: 10, borderRadius: 2,
                            background: d < s.sessions ? (s.status === "warn" ? "#c2842b" : "#1f3d2b") : "#e8e3d2",
                            opacity: p,
                          }}/>
                        ))}
                        <span style={{ fontSize: 10.5, color: "#6b7269", fontFamily: "var(--mono)", marginLeft: 4 }}>{s.sessions}/5</span>
                      </div>
                      <div style={{ textAlign: "right", color: "#8a8f86", fontFamily: "var(--mono)", fontSize: 10.5 }}>{s.last}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right column: weekly heatmap + leaderboard */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
              <Heatmap t={t}/>
              <Leaderboard t={t} students={students}/>
            </div>
          </div>
        </div>
      </AppShell>
    </div>
  );
}

function Counter({ value, progress }) {
  if (typeof value === "number") {
    return <span>{Math.round(value * progress)}</span>;
  }
  // string like "47%"
  const m = value.match(/^(\d+)(.*)$/);
  if (m) return <span>{Math.round(parseInt(m[1]) * progress)}{m[2]}</span>;
  return <span>{value}</span>;
}

function Sparkline({ data, progress, status }) {
  const w = 110, h = 22;
  const max = Math.max(...data, 1);
  const visibleCount = Math.ceil(data.length * progress);
  const pts = data.slice(0, visibleCount).map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / max) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  const areaPts = pts + ` ${(visibleCount-1)/(data.length-1)*w},${h} 0,${h}`;
  const color = status === "warn" ? "#c2842b" : "#1f3d2b";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polygon points={areaPts} fill={color} opacity="0.10"/>
      <polyline points={pts} stroke={color} strokeWidth="1.6" fill="none" strokeLinejoin="round" strokeLinecap="round"/>
      {visibleCount > 0 && (() => {
        const lastI = visibleCount - 1;
        const lastV = data[lastI];
        const x = (lastI / (data.length - 1)) * w;
        const y = h - (lastV / max) * (h - 4) - 2;
        return <circle cx={x} cy={y} r="2" fill={color}/>;
      })()}
    </svg>
  );
}

function Heatmap({ t }) {
  // 7 days x 4 weeks of lessons heatmap
  const data = [
    [1,2,1,0,3,1,0],
    [2,1,3,2,1,2,0],
    [1,2,2,3,2,1,0],
    [3,2,1,2,3,2,1],
  ];
  return (
    <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 12, padding: 16, opacity: Math.min(1, Math.max(0, (t - 1.6) / 0.5)) }}>
      <div style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 500 }}>Lesson load · last 4 weeks</div>
      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, fontFamily: "var(--mono)", fontSize: 9.5, color: "#8a8f86", justifyContent: "space-around", paddingTop: 12 }}>
          {["W-3","W-2","W-1","This"].map(l => <div key={l}>{l}</div>)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, fontFamily: "var(--mono)", fontSize: 9.5, color: "#8a8f86", marginBottom: 4, textAlign: "center" }}>
            {["M","T","W","T","F","S","S"].map((d,i) => <div key={i}>{d}</div>)}
          </div>
          {data.map((row, ri) => (
            <div key={ri} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
              {row.map((v, ci) => {
                const start = 1.7 + (ri * 7 + ci) * 0.018;
                const p = Math.min(1, Math.max(0, (t - start) / 0.3));
                const colors = ["#f3eedf","#cad8c0","#7a8c5a","#1f3d2b"];
                return (
                  <div key={ci} style={{
                    aspectRatio: "1.4",
                    background: colors[v] || "#f3eedf",
                    borderRadius: 3, opacity: p,
                  }}/>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10.5, color: "#6b7269", marginTop: 10, fontFamily: "var(--mono)" }}>
        Less {["#f3eedf","#cad8c0","#7a8c5a","#1f3d2b"].map((c,i) => <span key={i} style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }}/>)} More · 28 lessons this week
      </div>
    </div>
  );
}

function Leaderboard({ t, students }) {
  const top = [...students].sort((a,b) => b.trend[8] - a.trend[8]).slice(0, 4);
  return (
    <div style={{ background: "#fff", border: "1px solid #e8e3d2", borderRadius: 12, padding: 16, flex: 1, minHeight: 0, opacity: Math.min(1, Math.max(0, (t - 2.4) / 0.5)) }}>
      <div style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 500 }}>Most progress this month</div>
      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
        {top.map((s, i) => (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i<top.length-1 ? "1px solid #f3eedf" : "none" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#8a8f86", width: 16 }}>{i+1}.</div>
            <Avatar name={s.name} color="#5e8c7a" size={22}/>
            <div style={{ fontSize: 12, fontWeight: 500, flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
              {s.name} <FlagDot country={s.country} size={8}/>
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#1f3d2b", fontWeight: 600 }}>+{(s.trend[8] - s.trend[0]).toString()}pt</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { Scene5, Scene6, Scene7, Scene8 };
